import './styles.scss';

import _ from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter, WithRouterProps } from 'react-router';
import { Checkbox, Icon, Label, Loader, Table } from 'semantic-ui-react';

import AgeFromDate from 'app/components/AgeFromDate';
import DisplayDate from 'app/components/DisplayDate';
import {
  checkIsPicoxiaDicom,
  dicomDateTimeToDate,
  getDicomValue,
  getPatientInfoFromDicomData,
} from 'app/utils/dicom/DicomData';
import { Feedbacks } from 'app/interfaces/Image';
import PatternUnstructuredList from 'app/components/PatternUnstructuredList';
import { predictionsMerger } from 'app/utils/predictions/utils';
import {
  ABDOMEN_PATTERNS,
  THORAX_PATTERNS,
} from 'app/components/PatternStructuredList/patternsGroups';
import { AllPredictions } from 'app/interfaces/Predictions';
import VHSInlineDisplay from 'app/components/PatternUnstructuredList/VHSInlineDisplay';
import { ReactComponent as Picoxia } from '../../../static/svg_icons/picoxia_icon_wide_white_only.svg';
import { FindStudyResult } from 'app/queries/queryPACSStudiesQueryOptions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import getStudyQueryOptions, { GetStudyParams } from 'app/queries/getStudyQueryOptions';
import { Study } from 'app/interfaces/Study';
import preventDefault from 'app/utils/preventDefault';

const PATTERNS_TO_DISPLAY = [...Object.keys(THORAX_PATTERNS), ...Object.keys(ABDOMEN_PATTERNS)];
const THRESHOLD_FOR_DISPLAY = 0.5;

type StudyRowProps = WithRouterProps & {
  onClick?: () => void;
  images?: FindStudyResult[];
  selected?: boolean;
  isMultiSelected?: boolean;
  // Will `picoxiaStudyId` parameters with studyId in case a picoxia study was detected
  onMultiSelectChange?: (checked: boolean, picoxiaStudyId?: string) => void;
};

function StudyRowPrediction({
  isLoading,
  study,
  navigatetoStudy,
}: {
  isLoading: boolean;
  study: Study | null;
  navigatetoStudy: () => void;
}) {
  const aggregatedPredictions:
    | {
        predictions?: AllPredictions;
        feedback?: Feedbacks;
      }
    | undefined = useMemo(() => {
    if (!study) return undefined;

    const predictionsAggregate = {
      predictions: _.mergeWith(
        {},
        ...study.images.map(({ predictions }) => predictions),
        predictionsMerger
      ),
      feedback: _.merge({}, ...study.images.map(({ feedback }) => feedback)),
    };
    return predictionsAggregate;
  }, [study]);

  if (isLoading) return <Loader active inline="centered" />;

  if (!study) {
    return (
      <Label
        as="button"
        className="picoxia button open_ai_analysis"
        color="blue"
        onClick={navigatetoStudy}
      >
        <Picoxia />
        <FormattedMessage id="pacs.query.open_ai_analysis" />
      </Label>
    );
  }
  return (
    <PatternUnstructuredList
      image={aggregatedPredictions}
      patternsToDisplay={PATTERNS_TO_DISPLAY}
      thresholdForDisplay={THRESHOLD_FOR_DISPLAY}
      additionalPrefixPatterns={
        <VHSInlineDisplay patient={study?.patient} images={study?.images} />
      }
      hideConfidence
    />
  );
}

function StudyRow({
  images,
  router,
  onClick,
  selected,
  isMultiSelected,
  onMultiSelectChange,
}: StudyRowProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const referenceImage = images[0];
  const StudyID = getDicomValue(referenceImage.StudyID);
  const StudyInstanceUID = getDicomValue(referenceImage.StudyInstanceUID);
  const StudyDate = getDicomValue(referenceImage.StudyDate);
  const StudyTime = getDicomValue(referenceImage.StudyTime);
  const isPicoxiaStudy = checkIsPicoxiaDicom(referenceImage) && StudyID !== undefined;
  const studyInfoParams: GetStudyParams = { studyInstanceUID: StudyInstanceUID };
  if (isPicoxiaStudy) studyInfoParams.studyID = StudyID;

  const { isLoading, data: studyInfo } = useQuery({
    ...getStudyQueryOptions(studyInfoParams),
    staleTime: Infinity,
  });

  const studyDateTime = dicomDateTimeToDate(getDicomValue(StudyDate), getDicomValue(StudyTime));

  const patientInfo = useMemo(() => getPatientInfoFromDicomData(images[0]), [images[0]]);
  const { birth_date, sex, file_id, name, owner_name } = patientInfo || {};

  const picoxiaStudyId = isPicoxiaStudy && studyInfo?._id === StudyID ? StudyID : undefined;

  const navigatetoStudy = () => {
    // Invlaidation with 'none' is required here, else invalidation happends will queries are still active
    // thus they get instantly fetched again and we do not refetch them after navigation has happened.
    queryClient.invalidateQueries({
      ...getStudyQueryOptions(studyInfoParams),
      refetchType: 'none',
    });
    queryClient.invalidateQueries({ queryKey: [{ query: '/studies' }], refetchType: 'none' });
    if (picoxiaStudyId) {
      router.push(`/viewer?study_ids[]=${picoxiaStudyId}`);
    } else {
      router.push(`/viewer?studyInstanceUIDs[]=${StudyInstanceUID}`);
    }
  };

  return (
    <Table.Row
      onClick={onClick}
      className={selected ? 'selected' : ''}
      data-testid="study-row"
      onDoubleClick={navigatetoStudy}
      style={{ cursor: 'pointer' }}
    >
      <Table.Cell onClick={preventDefault} onDoubleClick={preventDefault}>
        <div>
          <Checkbox
            checked={isMultiSelected}
            aria-label={intl.formatMessage({ id: 'monitoring.study.select_for_comparison' })}
            onChange={(e, { checked }) => onMultiSelectChange(checked, picoxiaStudyId)}
          />
        </div>
      </Table.Cell>
      <Table.Cell>
        <DisplayDate date={studyDateTime} format="2-digit" />
      </Table.Cell>
      <Table.Cell>{file_id}</Table.Cell>
      <Table.Cell>{owner_name}</Table.Cell>
      <Table.Cell>{name}</Table.Cell>
      <Table.Cell>
        <AgeFromDate birthDate={birth_date} referenceDate={studyDateTime} />
      </Table.Cell>
      <Table.Cell>
        {sex && sex !== 'other' ? (
          <i
            className={`svg-icon ${sex}`}
            title={intl.formatMessage({ id: `patient_info.sex.${sex}` })}
            aria-label={intl.formatMessage({ id: `patient_info.sex.${sex}` })}
          />
        ) : null}
      </Table.Cell>
      <Table.Cell>{images.length}</Table.Cell>
      <Table.Cell>
        <StudyRowPrediction
          study={studyInfo}
          isLoading={isLoading}
          navigatetoStudy={navigatetoStudy}
        />
      </Table.Cell>
      <Table.Cell>
        <div className="flex center" style={{ height: '100%' }}>
          <Icon
            link
            data-testid="study-row-view"
            className="eye icon picoxia-fix"
            onClick={navigatetoStudy}
            title={intl.formatMessage({ id: 'general.see' })}
            aria-label={intl.formatMessage({ id: 'general.see' })}
          />
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

export default withRouter(StudyRow);
